import { SearchOutlined } from "@ant-design/icons";
import { Col, Input, Row, Select, Typography, Form } from "antd";
import React, { useEffect, useRef, useState } from "react";
import FilterSelect from "../device-filter/FilterSelect";
import {
  AppDispatch,
  dynamicSet,
  getKitList,
  getKitListRequest,
  getMyOrganization,
  getMyOrganizationRequest,
  useDynamicSelector,
} from "@src/redux";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import DynamicTreeSelect from "../../common/TreeSelect";
import LevelFilterContainer from "../../common/LevelFliter";
import { isArray } from "lodash";

const { Option } = Select;
const { Title, Text } = Typography;

const filterOptions = {
  validity: [
    { label: "All", value: "all" },
    { label: "Live", value: "live" },
    { label: "Expiring Soon", value: "expiring_soon" },
    { label: "Expired", value: "expired" },
    { label: "Not Set", value: "not_set" },
  ],
  operator: [
    { label: "All", value: "all" },
    { label: "Assigned", value: "assigned" },
    { label: "Unassigned", value: "unassigned" },
  ],
};

const ClientKitFilters: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const { pagination: kitPagination } = useDynamicSelector("kit_pagination");
  const { filter: savedFilter } = useDynamicSelector("kit_filter");
  const { result } = useSelector(getKitList);
  const { result: myOrganization } = useSelector(getMyOrganization);
  const [form] = Form.useForm();

  const operator = Form.useWatch("operator", form);
  const validity = Form.useWatch("validity", form);
  const organizationId = Form.useWatch("organizationId", form);

  // The org filter is applied only when the user explicitly drills into the
  // organization tree, or when viewing a client-specific page (route `id`).
  // When nothing is selected we intentionally send no org filter so that all
  // kits are shown by default (mirrors the device filter behaviour).
  const selectedTreeOrgId = Array.isArray(organizationId)
    ? [...organizationId].reverse().find(Boolean)
    : undefined;
  const orgFilterId = id || selectedTreeOrgId || undefined;

  const [searchValue, setSearchValue] = useState(
    savedFilter?.searchString || ""
  );
  const hasInitialized = useRef(false);
  const initialPagination = { pageLimit: 10, pageNumber: 1 };

  useEffect(() => {
    const operatorValue =
      savedFilter?.isOperatorAssigned === undefined
        ? "all"
        : savedFilter?.isOperatorAssigned
        ? "assigned"
        : "unassigned";
    form.setFieldsValue({
      search: savedFilter?.searchString || "",
      operator: operatorValue,
      validity: savedFilter?.validity || "all",
    });
    setSearchValue(savedFilter?.searchString || "");
  }, [form, savedFilter]);

  const buildFilters = (searchInput?: string) => {
    const operatorValue =
      !operator || operator === "all"
        ? undefined
        : operator === "unassigned"
        ? false
        : true;
    return {
      searchString: searchInput ?? searchValue,
      isOperatorAssigned: operatorValue,
      validity: !validity || validity === "all" ? undefined : validity,
      organizationId: orgFilterId,
    };
  };

  const requestList = (
    filters: Record<string, any>,
    pagination: { pageLimit: number; pageNumber: number }
  ) => {
    dispatch(dynamicSet("kit_filter", { filter: filters }));
    dispatch(dynamicSet("kit_pagination", { pagination }));
    dispatch(
      getKitListRequest({
        filter: filters,
        pagination,
      })
    );
  };

  const triggerSearch = (searchInput?: string) => {
    requestList(buildFilters(searchInput ?? ""), initialPagination);
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    requestList(buildFilters(savedFilter?.searchString), initialPagination);
  }, []);

  useEffect(() => {
    if (!hasInitialized.current) return;
    requestList(buildFilters(), initialPagination);
  }, [operator, validity, selectedTreeOrgId, id]);

  return (
    <div className="filter-container">
      <div>
        <Row className="header-filter" align="middle">
          <Col span={24}>
            <Title level={4} className="client-filters-header-title">
              Filters
            </Title>
          </Col>
        </Row>
        <Form layout="vertical" className="common-filters" form={form}>
          <Col span={24}>
            <Form.Item name="search" label="Search">
              <Input
                prefix={<SearchOutlined />}
                className="filter-input"
                allowClear
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  // If cleared, trigger API
                  if (e.target.value === "") {
                    form.setFieldsValue({ search: "" });
                    triggerSearch("");
                  }
                }}
                onPressEnter={(e) => {
                  form.setFieldsValue({ search: searchValue });
                  triggerSearch(searchValue);
                }}
              />
            </Form.Item>
          </Col>
          <Form.Item name="operator" label="Operator" initialValue="all">
            <Select className="filter-select">
              {filterOptions.operator.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="validity" label="Validity" initialValue="all">
            <Select className="filter-select">
              {filterOptions.validity.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {!id && (
            <Row gutter={24}>
              <DynamicTreeSelect
                form={form}
                col={24}
                mutationType="add"
                isRequired={false}
                allowClear={true}
                className="check"
                organizationId={id}
              />
            </Row>
          )}
        </Form>
      </div>
      <Row className="footer">
        <Col>
          <Text strong style={{ fontSize: "20px", fontFamily: "Abel" }}>
            TOTAL
          </Text>
        </Col>
        <Col className="total-value">
          <Text strong style={{ color: "#ffffff", fontSize: "17px" }}>
            {result?.pagination?.totalCount || result?.items?.length || 0}
          </Text>
        </Col>
      </Row>
    </div>
  );
};

export default ClientKitFilters;
